import React, { useState, useEffect, useRef } from 'react';
import { X } from 'react-feather';

interface TagInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags?: string[];
  placeholder?: string;
  disabled?: boolean;
}

export const TagInput: React.FC<TagInputProps> = ({
  tags,
  onTagsChange,
  availableTags = [],
  placeholder = 'Add tags...',
  disabled = false
}) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on user input for autocomplete
  // Only depends on [input] to avoid infinite loops caused by changing tags/availableTags arrays
  useEffect(() => {
    if (input.trim()) {
      // Show matching tags that aren't already selected (case-insensitive)
      const filtered = availableTags.filter(
        tag =>
          tag.toLowerCase().includes(input.toLowerCase()) &&
          !tags.includes(tag)
      );
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      // Clear suggestions when input is empty to avoid showing stale data
      setSuggestions([]);
      setShowSuggestions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const handleAddTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      onTagsChange([...tags, trimmedTag]);
      setInput('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleRemoveTag = (tag: string) => {
    onTagsChange(tags.filter(t => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (input.trim()) {
        handleAddTag(input);
      }
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      // Delete last tag when backspace is pressed on empty input (UX pattern)
      handleRemoveTag(tags[tags.length - 1]);
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)) {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="space-y-2">
      <div className="relative">
        <div className="flex flex-wrap gap-1.5 p-2 min-h-[40px] border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-eddy-blue">
          {tags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-eddy-blue/10 text-eddy-blue border border-eddy-blue/20 rounded-full text-xs font-medium"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                disabled={disabled}
                className="ml-1 hover:text-eddy-blue/70 disabled:opacity-50"
              >
                <X size={14} />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => input && setShowSuggestions(true)}
            placeholder={tags.length === 0 ? placeholder : ''}
            disabled={disabled}
            className="flex-1 outline-none bg-transparent text-sm min-w-[100px]"
          />
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto"
          >
            {suggestions.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => handleAddTag(tag)}
                className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Show "Create new tag" option if input doesn't match any suggestion */}
      {input.trim() && !availableTags.includes(input.trim()) && !tags.includes(input.trim()) && (
        <button
          type="button"
          onClick={() => handleAddTag(input)}
          className="text-sm text-eddy-blue hover:text-eddy-blue/70 px-2"
        >
          + Create new tag: "{input}"
        </button>
      )}
    </div>
  );
};
